import { Box, Card, Icon, Paragraph, Tag, TextButton, Tooltip, TriggerButton } from '@zillow/constellation-10';
import { IconFileFilled } from '@zillow/constellation-icons';
import type { FailedUpload } from '../../../../../hooks/useIncomeDocumentUploads';

function getReason(reason: FailedUpload['reason']) {
  switch (reason) {
    case 'type':
      return 'Use a .pdf, .jpg or .png file.';
    case 'tooLarge':
      return 'This file is larger than 100MB.';
    case 'upload':
      return 'Check your connection, then select Retry.';
    case 'rejected':
      return 'Password-protected files aren’t supported.';
    case 'save':
      return 'We couldn’t save this file.';
    case 'attach':
      return 'This file is saved, but it isn’t added here yet. Select Retry.';
    case 'limit':
      return 'This income source has the most files allowed.';
  }
}

export type FailedUploadActions = { onRetry: (key: string) => void; onRemove: (key: string) => void };

export type FailedUploads = FailedUploadActions & { uploads: Array<FailedUpload> };

export function FailedUploadCard({ upload, onRetry, onRemove }: FailedUploadActions & { upload: FailedUpload }) {
  const isUploadError = upload.reason === 'upload' || (upload.reason === 'save' && upload.canRetry);

  return (
    <Card
      elevated={false}
      tone="soft"
      body={
        <>
          <Box
            css={{
              position: 'relative',
              height: '2400',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Icon size="lg">
              <IconFileFilled />
            </Icon>
            <Tag tone="critical" size="sm" css={{ position: 'absolute', top: 0, right: 0 }}>
              {isUploadError ? 'Upload failed' : 'Not added'}
            </Tag>
          </Box>
          <Box css={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 'tighter' }}>
            <Paragraph
              textStyle="fineprint-bold"
              css={{ overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis' }}
            >
              {upload.fileName}
            </Paragraph>
            <Tooltip
              content={getReason(upload.reason)}
              trigger={<TriggerButton aria-label={`Why ${upload.fileName} wasn’t added`} />}
            />
          </Box>
          <Box css={{ display: 'flex', flexWrap: 'wrap', gap: 'tight' }}>
            {upload.canRetry && (
              <TextButton
                type="button"
                textStyle="body-sm-bold"
                aria-label={`Retry ${upload.fileName}`}
                onClick={() => onRetry(upload.key)}
              >
                Retry
              </TextButton>
            )}
            <TextButton
              type="button"
              textStyle="body-sm-bold"
              aria-label={`Remove ${upload.fileName}`}
              onClick={() => onRemove(upload.key)}
            >
              Remove
            </TextButton>
          </Box>
        </>
      }
    />
  );
}
